import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    Box,
    TextField,
    Button,
    Typography,
    Avatar,
    Grid,
    Card,
    CardContent,
    Divider,
    Stack,
    Alert,
    Snackbar,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import {
    Edit as EditIcon,
    LinkedIn as LinkedInIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    Save as SaveIcon,
    PhotoCamera as PhotoCameraIcon,  // ADD THIS LINE

} from '@mui/icons-material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { EmailConnection } from '../components/EmailConnection';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import SubscriptionInfo from '../components/SubscriptionInfo';
const BACKEND_URL = process.env.REACT_APP_BACK_END_URL;

const DeleteDialog = ({
    open,
    onClose,
    onDelete,
    isDeleting,
    deleteProgress,
    userEmail,
    deleteConfirmation,
    setDeleteConfirmation
}) => (
    <Dialog
        open={open}
        onClose={() => !isDeleting && onClose()}
        maxWidth="sm"
        fullWidth
    >
        <DialogTitle sx={{
            bgcolor: 'error.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
        }}>
            <DeleteIcon />
            Delete Account
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
            {isDeleting ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={48} color="error" />
                    <Typography sx={{ mt: 2 }}>
                        Deleting account and associated data...
                        {deleteProgress.total > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Deleting automations: {deleteProgress.current} of {deleteProgress.total}
                            </Typography>
                        )}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography gutterBottom>
                        Are you absolutely sure you want to delete your account? This action cannot be undone and will:
                    </Typography>
                    <Box component="ul" sx={{ mt: 2 }}>
                        <Typography component="li">Delete all your saved data</Typography>
                        <Typography component="li">Remove your profile information</Typography>
                        <Typography component="li">Delete your email preferences</Typography>
                        <Typography component="li">Cancel all automated follow-ups</Typography>
                    </Box>
                    <Typography sx={{ mt: 2 }} color="error">
                        Please type your email address to confirm:
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        margin="normal"
                        placeholder={userEmail}
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                    />
                </>
            )}
        </DialogContent>
        {!isDeleting && (
            <DialogActions sx={{ p: 3 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                    disabled={deleteConfirmation !== userEmail}
                >
                    Delete my account
                </Button>
            </DialogActions>
        )}
    </Dialog>
);

export const Settings = () => {
    const theme = useTheme();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const { deleteAccount } = useAuth();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [userData, setUserData] = useState({
        name: '',
        linkedinUrl: '',
        phoneNumber: '',
        resumeUrl: null,
        resumeFileName: null,
        photoUrl: null, // ADD THIS LINE
    });
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });
    const [resumeFile, setResumeFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false); // ADD THIS LINE

    const [resumeData, setResumeData] = useState({
        fullName: '',
        phone: '',
        email: '',
        country: '',
        city: '',
        linkedinUrl: '',
        githubUrl: '',
        education: []
    });
    const [showResumeDataForm, setShowResumeDataForm] = useState(false);
    const [missingFields, setMissingFields] = useState([]);



    // Update your useEffect to fetch ALL user data including resume
    useEffect(() => {
        const fetchUserData = async () => {
            if (user?.email) {
                try {
                    const docRef = doc(db, 'users', user.email);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        console.log('Fetched user data:', data); // Debug log
                        setUserData({
                            name: data.name || '',
                            linkedinUrl: data.linkedinUrl || '',
                            phoneNumber: data.phoneNumber || '',
                            resumeUrl: data.resumeUrl || null,
                            resumeFileName: data.resumeFileName || null,
                            photoUrl: data.photoUrl || null, // ADD THIS LINE
                        });
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                    setError({
                        show: true,
                        message: 'Failed to load user data',
                        severity: 'error'
                    });
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchUserData();
    }, [user]);

    const handleUpdateProfile = async () => {
        if (user?.email) {
            const userRef = doc(db, 'users', user.email);
            await updateDoc(userRef, {
                name: userData.name,
                linkedinUrl: userData.linkedinUrl
            });
            setIsEditing(false);
            setShowSuccess(true);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
        setError({ show: true, message: 'Only JPG, PNG, and WEBP images are allowed', severity: 'error' });
        return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
        setError({ show: true, message: 'Image size must be less than 2MB', severity: 'error' });
        return;
    }

    setUploadingPhoto(true); // ADD THIS

    try {
        // Create storage reference
        const storageRef = ref(storage, `profile-photos/${user.email}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            },
            (error) => {
                console.error('Upload error:', error);
                setError({ show: true, message: 'Failed to upload photo', severity: 'error' });
                setUploadingPhoto(false); // ADD THIS
            },
            async () => {
                // Get download URL
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                // Update Firestore
                const userRef = doc(db, 'users', user.email);
                await updateDoc(userRef, {
                    photoUrl: downloadURL,
                });

                // Update local state
                setUserData(prev => ({
                    ...prev,
                    photoUrl: downloadURL
                }));

                setShowSuccess(true);
                setUploadingPhoto(false); // ADD THIS
                setUploadProgress(0); // ADD THIS
            }
        );
    } catch (error) {
        console.error('Error uploading photo:', error);
        setError({ show: true, message: 'Failed to upload photo', severity: 'error' });
        setUploadingPhoto(false); // ADD THIS
    }
};

const handleDeletePhoto = async () => {
    try {
        if (userData.photoUrl) {
            // Delete from Storage
            const photoRef = ref(storage, userData.photoUrl);
            await deleteObject(photoRef);
        }

        // Update Firestore
        const userRef = doc(db, 'users', user.email);
        await updateDoc(userRef, {
            photoUrl: null,
        });

        // Update local state
        setUserData(prev => ({
            ...prev,
            photoUrl: null
        }));

        setShowSuccess(true);
    } catch (error) {
        console.error('Error deleting photo:', error);
        setError({ show: true, message: 'Failed to delete photo', severity: 'error' });
    }
};

    const handleDeleteAccount = async () => {
        setIsDeleting(true);
        setError({ show: false, message: '', severity: 'error' });
        try {
            // 1. Query all email automations for this user
            const automationsRef = collection(db, 'emailAutomations');
            const q = query(automationsRef, where('userEmail', '==', user.email));
            const querySnapshot = await getDocs(q);

            const automationDocs = querySnapshot.docs;
            setDeleteProgress({ current: 0, total: automationDocs.length });

            // 2. Delete all automations in batches
            const batchSize = 500;
            for (let i = 0; i < automationDocs.length; i += batchSize) {
                const batch = writeBatch(db);
                const batchDocs = automationDocs.slice(i, i + batchSize);

                batchDocs.forEach(doc => {
                    batch.delete(doc.ref);
                });

                await batch.commit();
                setDeleteProgress(prev => ({
                    ...prev,
                    current: Math.min(i + batchSize, automationDocs.length)
                }));
            }

            // 3. Delete the user account
            await deleteAccount();

            // 4. Navigate after short delay
            setShowSuccess(true);
            setTimeout(() => {
                navigate('/');
            }, 1500);

        } catch (error) {
            console.error('Failed to delete account:', error);
            let errorMessage = 'Failed to delete account. Please try again.';

            if (error.code === 'permission-denied') {
                errorMessage = 'You do not have permission to delete this account.';
            } else if (error.code === 'not-found') {
                errorMessage = 'Account not found.';
            } else if (error.code === 'network-error') {
                errorMessage = 'Network error. Please check your connection and try again.';
            }

            setError({ show: true, message: errorMessage, severity: 'error' });
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
        }
    };


    // Add resume upload handler:
    // Add resume upload handler:
    const handleResumeUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['application/pdf', 'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            setError({ show: true, message: 'Only PDF and Word documents are allowed', severity: 'error' });
            return;
        }

        // Validate file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError({ show: true, message: 'File size must be less than 5MB', severity: 'error' });
            return;
        }

        setUploading(true);

        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('resume', file);
            formData.append('user_email', user.email);

            console.log('📤 Uploading resume to backend...');

            // Upload to backend endpoint
            const response = await fetch(
                `${BACKEND_URL}/applybridge/upload-resume`,
                {
                    method: 'POST',
                    body: formData
                    // Don't set Content-Type header - browser will set it with boundary for FormData
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to upload resume');
            }

            const result = await response.json();
            console.log('✅ Resume uploaded successfully:', result);

            // Update local state with the response
            setUserData(prev => ({
                ...prev,
                resumeUrl: result.resumeUrl,
                resumeFileName: file.name,
                resumeParsedData: result.parsedData
            }));

            // Check if manual input is needed
            if (result.needsManualInput && result.missingFields.length > 0) {
                setMissingFields(result.missingFields);
                setResumeData(result.parsedData);
                setShowResumeDataForm(true);
                setError({
                    show: true,
                    message: 'Resume uploaded! Please fill in the missing information.',
                    severity: 'info'
                });
            } else {
                setShowSuccess(true);
                setError({
                    show: true,
                    message: 'Resume uploaded and parsed successfully!',
                    severity: 'success'
                });
            }

        } catch (error) {
            console.error('❌ Error uploading resume:', error);
            setError({
                show: true,
                message: error.message || 'Failed to upload resume',
                severity: 'error'
            });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteResume = async () => {
        try {
            // Delete from Storage
            const storageRef = ref(storage, `resumes/${user.email}/${userData.resumeFileName}`);
            await deleteObject(storageRef);

            // Update Firestore
            const userRef = doc(db, 'users', user.email);
            await updateDoc(userRef, {
                resumeUrl: null,
                resumeFileName: null,
                resumeUploadedAt: null,
                resumeFileType: null
            });

            // Update local state
            setUserData(prev => ({
                ...prev,
                resumeUrl: null,
                resumeFileName: null
            }));

            setShowSuccess(true);
        } catch (error) {
            console.error('Error deleting resume:', error);
            setError({ show: true, message: 'Failed to delete resume', severity: 'error' });
        }
    };

    const handleSaveResumeData = async () => {
        try {
            console.log('💾 Saving resume data...');

            const response = await fetch(
                `${BACKEND_URL}/applybridge/update-resume-data`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: user.email,
                        resumeData: resumeData
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to update resume data');
            }

            console.log('✅ Resume data saved successfully');

            setShowResumeDataForm(false);
            setShowSuccess(true);
            setError({
                show: true,
                message: 'Resume information saved successfully!',
                severity: 'success'
            });

            // Reload user data
            const docRef = doc(db, 'users', user.email);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setUserData({
                    name: data.name || '',
                    linkedinUrl: data.linkedinUrl || '',
                    phoneNumber: data.phoneNumber || '',
                    resumeUrl: data.resumeUrl || null,
                    resumeFileName: data.resumeFileName || null,
                });
            }

        } catch (error) {
            console.error('❌ Error saving resume data:', error);
            setError({
                show: true,
                message: error.message || 'Failed to save resume data',
                severity: 'error'
            });
        }
    };

    // Helper functions for education management
    const addEducation = () => {
        setResumeData(prev => ({
            ...prev,
            education: [...prev.education, {
                school: '',
                degree: '',
                discipline: '',
                startDate: '',
                endDate: ''
            }]
        }));
    };

    const removeEducation = (index) => {
        setResumeData(prev => ({
            ...prev,
            education: prev.education.filter((_, i) => i !== index)
        }));
    };

    const updateEducation = (index, field, value) => {
        setResumeData(prev => ({
            ...prev,
            education: prev.education.map((edu, i) =>
                i === index ? { ...edu, [field]: value } : edu
            )
        }));
    };

    return (
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                p: 4,
                backgroundColor: theme.palette.blue['900']
            }}>
                <Box sx={{ width: '100%', maxWidth: 1200 }}>
                    <Grid container spacing={4} justifyContent="center">
                        

                        <Grid item xs={12}>
    <Card>
        <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Profile Information</Typography>
                <Button
                    onClick={() => setIsEditing(!isEditing)}
                    startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                >
                    {isEditing ? 'Save' : 'Edit'}
                </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={4}>
                {/* Left side - Profile Photo and Basic Info */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={3} alignItems="center">
                        <Box sx={{ position: 'relative' }}>
    <Avatar
        src={userData.photoUrl}
        sx={{
            width: 120,
            height: 120,
            bgcolor: 'primary.main',
            fontSize: '3rem',
            opacity: uploadingPhoto ? 0.5 : 1, // Dim avatar while uploading
        }}
    >
        {!userData.photoUrl && userData.name.charAt(0)}
    </Avatar>
    
    {/* Loading Spinner Overlay */}
    {uploadingPhoto && (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
            }}
        >
            <CircularProgress size={40} />
        </Box>
    )}
    
    {isEditing && !uploadingPhoto && (
        <>
            <input
                accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }}
                id="photo-upload"
                type="file"
                onChange={handlePhotoUpload}
            />
            <label htmlFor="photo-upload">
                <IconButton
                    component="span"
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                            bgcolor: 'primary.dark',
                        },
                    }}
                >
                    <PhotoCameraIcon />
                </IconButton>
            </label>
            {userData.photoUrl && (
                <IconButton
                    onClick={handleDeletePhoto}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': {
                            bgcolor: 'error.dark',
                        },
                    }}
                    size="small"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )}
        </>
    )}
</Box>
                        <Typography variant="h5" gutterBottom>
                            {userData.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {user?.email}
                        </Typography>
                        <SubscriptionInfo user={user} />
                    </Stack>
                </Grid>

                {/* Right side - Editable Fields */}
                <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <PersonIcon color="primary" />
                            <TextField
                                fullWidth
                                label="Name"
                                value={userData.name}
                                disabled={!isEditing}
                                onChange={(e) => setUserData(prev => ({ ...prev, name: e.target.value }))}
                                variant={isEditing ? "outlined" : "filled"}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <EmailIcon color="primary" />
                            <TextField
                                fullWidth
                                label="Email"
                                value={user?.email || ''}
                                disabled
                                variant="filled"
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <LinkedInIcon color="primary" />
                            <TextField
                                fullWidth
                                label="LinkedIn URL"
                                value={userData.linkedinUrl}
                                disabled={!isEditing}
                                onChange={(e) => setUserData(prev => ({ ...prev, linkedinUrl: e.target.value }))}
                                variant={isEditing ? "outlined" : "filled"}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <LinkedInIcon color="primary" />
                            <TextField
                                fullWidth
                                label="Phone Number"
                                value={userData.phoneNumber}
                                disabled={!isEditing}
                                onChange={(e) => setUserData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                variant={isEditing ? "outlined" : "filled"}
                            />
                        </Box>
                        {isEditing && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpdateProfile}
                                sx={{ mt: 2 }}
                                startIcon={<SaveIcon />}
                            >
                                Save Changes
                            </Button>
                        )}
                    </Stack>
                </Grid>
            </Grid>
        </CardContent>
    </Card>
</Grid>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Resume
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />

                                    {userData.resumeUrl ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <DescriptionIcon color="primary" />
                                            <Typography sx={{ flex: 1 }}>
                                                {userData.resumeFileName}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                onClick={() => window.open(userData.resumeUrl, '_blank')}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={handleDeleteResume}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <input
                                                accept=".pdf,.doc,.docx"
                                                style={{ display: 'none' }}
                                                id="resume-upload"
                                                type="file"
                                                onChange={handleResumeUpload}
                                                disabled={uploading}
                                            />
                                            <label htmlFor="resume-upload">
                                                <Button
                                                    variant="contained"
                                                    component="span"
                                                    startIcon={<CloudUploadIcon />}
                                                    disabled={uploading}
                                                >
                                                    Upload Resume
                                                </Button>
                                            </label>
                                            {uploading && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Uploading: {Math.round(uploadProgress)}%
                                                    </Typography>
                                                    <Box sx={{ width: '100%', mt: 1 }}>
                                                        <div style={{
                                                            width: `${uploadProgress}%`,
                                                            height: 4,
                                                            backgroundColor: theme.palette.primary.main,
                                                            transition: 'width 0.3s'
                                                        }} />
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Resume Data Form - Shows when manual input is needed */}
                        {showResumeDataForm && (
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Complete Your Resume Information
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Please fill in the following information that we couldn't extract from your resume:
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />

                                        <Stack spacing={3}>
                                            {missingFields.includes('phone') && (
                                                <TextField
                                                    fullWidth
                                                    label="Phone Number (with country code)"
                                                    placeholder="+1 234 567 8900"
                                                    value={resumeData.phone}
                                                    onChange={(e) => setResumeData(prev => ({ ...prev, phone: e.target.value }))}
                                                />
                                            )}

                                            {missingFields.includes('country') && (
                                                <TextField
                                                    fullWidth
                                                    label="Country"
                                                    value={resumeData.country}
                                                    onChange={(e) => setResumeData(prev => ({ ...prev, country: e.target.value }))}
                                                />
                                            )}

                                            {missingFields.includes('city') && (
                                                <TextField
                                                    fullWidth
                                                    label="City"
                                                    value={resumeData.city}
                                                    onChange={(e) => setResumeData(prev => ({ ...prev, city: e.target.value }))}
                                                />
                                            )}

                                            {missingFields.includes('githubUrl') && (
                                                <TextField
                                                    fullWidth
                                                    label="GitHub Profile URL"
                                                    placeholder="https://github.com/username"
                                                    value={resumeData.githubUrl}
                                                    onChange={(e) => setResumeData(prev => ({ ...prev, githubUrl: e.target.value }))}
                                                />
                                            )}

                                            {missingFields.includes('education') && (
                                                <Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                        <Typography variant="subtitle1" fontWeight="600">
                                                            Education
                                                        </Typography>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={addEducation}
                                                            startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>}
                                                        >
                                                            Add Education
                                                        </Button>
                                                    </Box>

                                                    {resumeData.education && resumeData.education.length === 0 && (
                                                        <Alert severity="info" sx={{ mb: 2 }}>
                                                            Click "Add Education" to add your educational background
                                                        </Alert>
                                                    )}

                                                    {resumeData.education && resumeData.education.map((edu, index) => (
                                                        <Card key={index} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                                                            <Stack spacing={2}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="subtitle2" color="primary">
                                                                        Education #{index + 1}
                                                                    </Typography>
                                                                    <Button
                                                                        color="error"
                                                                        size="small"
                                                                        onClick={() => removeEducation(index)}
                                                                        startIcon={<DeleteIcon />}
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </Box>

                                                                <TextField
                                                                    fullWidth
                                                                    label="School/University"
                                                                    placeholder="e.g., Stanford University"
                                                                    value={edu.school}
                                                                    onChange={(e) => updateEducation(index, 'school', e.target.value)}
                                                                />

                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Degree"
                                                                            placeholder="Bachelor's, Master's, PhD"
                                                                            value={edu.degree}
                                                                            onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Discipline/Major"
                                                                            placeholder="Computer Science"
                                                                            value={edu.discipline}
                                                                            onChange={(e) => updateEducation(index, 'discipline', e.target.value)}
                                                                        />
                                                                    </Grid>
                                                                </Grid>

                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Start Date"
                                                                            type="month"
                                                                            value={edu.startDate}
                                                                            onChange={(e) => updateEducation(index, 'startDate', e.target.value)}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="End Date"
                                                                            type="month"
                                                                            value={edu.endDate}
                                                                            onChange={(e) => updateEducation(index, 'endDate', e.target.value)}
                                                                            InputLabelProps={{ shrink: true }}
                                                                            helperText="Leave empty if currently enrolled"
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Stack>
                                                        </Card>
                                                    ))}
                                                </Box>
                                            )}

                                            <Divider />

                                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setShowResumeDataForm(false)}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSaveResumeData}
                                                    startIcon={<SaveIcon />}
                                                >
                                                    Save Information
                                                </Button>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <EmailConnection />
                        </Grid>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="error" gutterBottom>
                                        Danger Zone
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Delete Account
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Once you delete your account, there is no going back. Please be certain.
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => setDeleteDialogOpen(true)}
                                        >
                                            Delete Account
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>


                    </Grid>
                </Box>

                <Snackbar
                    open={showSuccess}
                    autoHideDuration={6000}
                    onClose={() => setShowSuccess(false)}
                >
                    <Alert severity="success" variant="filled">
                        Profile updated successfully!
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={error?.show}
                    autoHideDuration={6000}
                    onClose={() => setError({ ...error, show: false })}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        severity={error?.severity || 'error'}
                        variant="filled"
                        onClose={() => setError({ ...error, show: false })}
                    >
                        {error?.message}
                    </Alert>
                </Snackbar>

                <DeleteDialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    onDelete={handleDeleteAccount}
                    isDeleting={isDeleting}
                    deleteProgress={deleteProgress}
                    userEmail={user?.email}
                    deleteConfirmation={deleteConfirmation}
                    setDeleteConfirmation={setDeleteConfirmation}
                />
            </Box>
        </GoogleOAuthProvider>
    );
};

export default Settings